"use client";

import React from "react";
import { ShieldAlert, Cpu, Code, Network, Search, Smartphone, Mail, Phone, MapPin, ChevronRight, Activity, CheckCircle2, MonitorPlay, Fingerprint, Lock, ShieldCheck, Zap } from "lucide-react";

export default function Home() {
  return (
    <div className="min-h-screen bg-[#050B14] text-slate-200 font-sans selection:bg-blue-600 selection:text-white">
      
      {/* 1. Header (Logo Resmi Terpasang) */}
      <header className="fixed top-0 w-full z-50 bg-[#050B14]/80 backdrop-blur-lg border-b border-blue-900/30">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          
          {/* Logo Asli */}
          <div className="flex items-center gap-3 cursor-pointer group">
            <div className="relative w-11 h-11 rounded-full overflow-hidden border border-blue-500/50 shadow-[0_0_15px_rgba(59,130,246,0.4)] group-hover:scale-105 transition-transform">
              <img 
                src="/logo-juragan.png" 
                alt="Logo Juragan Langit Teknologi" 
                className="w-full h-full object-cover"
              />
            </div>
            <span className="text-xl font-bold tracking-widest text-white font-mono">JURAGAN IT</span>
          </div>

          <nav className="hidden md:flex gap-8 text-xs font-bold tracking-widest text-slate-400 font-mono">
            <a href="#about" className="hover:text-blue-400 transition-colors">ABOUT</a>
            <a href="#capabilities" className="hover:text-blue-400 transition-colors">CAPABILITIES</a>
            <a href="#products" className="hover:text-blue-400 transition-colors">PRODUCTS & AI</a>
            <a href="#contact" className="hover:text-blue-400 transition-colors">CONTACT</a>
          </nav>
        </div>
      </header>

      {/* 2. Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-52 md:pb-40 px-6 flex flex-col items-center justify-center text-center overflow-hidden min-h-[90vh]">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072&auto=format&fit=crop" 
            alt="Cyber Network Background" 
            className="w-full h-full object-cover opacity-40 mix-blend-luminosity animate-[pulse_10s_ease-in-out_infinite]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050B14]/60 via-[#050B14]/80 to-[#050B14]"></div>
        </div>
        <div className="absolute inset-0 z-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
        
        <div className="relative z-10 max-w-4xl mx-auto mt-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-800/50 bg-blue-900/40 backdrop-blur-md text-blue-400 text-xs font-mono tracking-widest mb-8">
            <Activity className="w-4 h-4 animate-pulse text-blue-400" /> PT JURAGAN LANGIT TEKNOLOGI
          </div>
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-white tracking-tighter mb-6 leading-tight drop-shadow-2xl">
            BUILT TO <br className="md:hidden" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-500 filter drop-shadow-[0_0_20px_rgba(59,130,246,0.5)]">
              DEFEND.
            </span>
          </h1>
          <p className="text-lg md:text-xl text-slate-300 mb-12 tracking-wider font-mono max-w-2xl mx-auto drop-shadow-md">
            TECHNOLOGY ENGINEERED FOR DIGITAL RESILIENCE.
          </p>
          <a href="#contact" className="group relative inline-flex items-center gap-3 px-8 py-4 bg-blue-600 text-white font-bold tracking-wider font-mono overflow-hidden rounded-md transition-all hover:scale-105 hover:shadow-[0_0_40px_rgba(37,99,235,0.6)] border border-blue-500">
            <span className="relative z-10">START A CONVERSATION</span>
            <ChevronRight className="w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </section>

      {/* 3. Evolution / About */}
      <section id="about" className="py-28 px-6 bg-[#03070f] relative border-y border-blue-900/20">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-20">
            <div className="lg:col-span-5">
              <span className="text-xs font-mono font-bold text-blue-500 tracking-[0.25em] uppercase block mb-3">
                // ABOUT JURAGAN IT
              </span>
              <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight leading-tight">
                TECHNOLOGY ENGINEERED FOR DIGITAL RESILIENCE.
              </h2>
            </div>
            <div className="lg:col-span-7 border-l-2 border-blue-500/40 pl-6 lg:pl-10">
              <p className="text-slate-300 text-base md:text-lg leading-relaxed">
                <strong className="text-white">Juragan IT</strong> adalah identitas bisnis PT Juragan Langit Teknologi, perusahaan teknologi Indonesia dengan <span className="text-blue-400 font-semibold">Cyber Security sebagai kompetensi utama</span>. Artificial Intelligence, Digital Forensic, Software Development, dan System Integration memperkuat solusi agar aman, relevan, dan dapat dipertanggungjawabkan.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { num: "01", title: "TECHNOLOGY FOUNDATION", desc: "Memahami kebutuhan bisnis, software, jaringan, perangkat, dan infrastruktur sebagai satu ekosistem yang utuh." },
              { num: "02", title: "SECURITY BY DESIGN", desc: "Menempatkan Cyber Security dan Digital Forensic sejak tahap awal arsitektur, pengembangan, hingga integrasi sistem." },
              { num: "03", title: "INTELLIGENCE-DRIVEN", desc: "Memanfaatkan kecerdasan buatan (AI), data analitik, dan otomasi untuk mempercepat pemahaman konteks serta respons." }
            ].map((item, idx) => (
              <div key={idx} className="group relative p-8 rounded-2xl bg-slate-900/60 backdrop-blur-md border border-slate-800 hover:border-blue-500/60 transition-all duration-300 hover:-translate-y-1 flex flex-col justify-between">
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <span className="text-3xl font-black font-mono text-blue-500/40 group-hover:text-blue-400 transition-colors">{item.num}</span>
                    <span className="w-2 h-2 rounded-full bg-blue-500/40 group-hover:bg-blue-400 animate-pulse"></span>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-3 tracking-wide">{item.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
                <div className="mt-6 pt-4 border-t border-slate-800/80 flex items-center gap-2 text-xs font-mono text-blue-400 opacity-80 group-hover:opacity-100">
                  <span>SYSTEM ACTIVE</span> <ChevronRight className="w-3 h-3" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Products & AI Showcase */}
      <section id="products" className="py-32 px-6 bg-[#050B14] relative overflow-hidden">
        <div className="absolute inset-0 z-0">
           <img 
             src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=2070&auto=format&fit=crop" 
             alt="Server Room" 
             className="w-full h-full object-cover opacity-10 mix-blend-screen"
           />
           <div className="absolute inset-0 bg-gradient-to-t from-[#050B14] via-[#050B14]/90 to-[#050B14]"></div>
        </div>
        
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-purple-900/10 rounded-full blur-[120px] mix-blend-screen pointer-events-none z-0"></div>

        <div className="relative z-10 max-w-7xl mx-auto text-center mb-20">
          <h2 className="text-xs font-mono font-black text-purple-500 tracking-[0.2em] mb-4">// JURAGANIT PRODUCTS</h2>
          <p className="text-3xl md:text-5xl font-bold text-white">PRODUCTS THAT TURN CAPABILITY <br/> INTO OPERATIONAL VALUE.</p>
        </div>

        {/* OXXA */}
        <div className="relative z-10 max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-16 mb-32 bg-slate-900/60 backdrop-blur-md border border-slate-700/50 rounded-3xl p-8 lg:p-16 shadow-2xl">
          <div className="flex-1 space-y-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full overflow-hidden border border-blue-400">
                  <img src="/logo-juragan.png" alt="Logo" className="w-full h-full object-cover" />
                </div>
                <h3 className="text-3xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500 tracking-wider font-mono">OXXA</h3>
              </div>
              <p className="text-xs font-mono font-bold text-slate-400 tracking-widest mb-3">PERSONAL CYBER SECURITY</p>
              <h4 className="text-2xl font-bold text-white leading-tight">SECURITY YOU CAN SEE, UNDERSTAND, AND ACT ON.</h4>
            </div>

            <p className="text-slate-300 leading-relaxed">
              OXXA membantu pengguna memahami kondisi perangkat, risiko aplikasi dan berkas, aktivitas login, serta indikasi penipuan dalam satu pengalaman yang sederhana.
            </p>

            <div className="grid grid-cols-2 gap-4 pt-2">
              <div className="p-4 rounded-2xl bg-[#0a101d] border border-slate-800">
                <ShieldCheck className="w-6 h-6 text-emerald-400 mb-2" />
                <h5 className="text-white font-bold text-sm">Real-time Protection</h5>
                <p className="text-slate-400 text-xs mt-1">Pemantauan aktivitas perangkat mandiri secara berkala.</p>
              </div>
              <div className="p-4 rounded-2xl bg-[#0a101d] border border-slate-800">
                <Zap className="w-6 h-6 text-blue-400 mb-2" />
                <h5 className="text-white font-bold text-sm">Fraud Detection</h5>
                <p className="text-slate-400 text-xs mt-1">Deteksi dini indikasi penipuan & anomali login.</p>
              </div>
            </div>

            <div className="space-y-3 pt-2">
              {["Device Status Monitoring", "App & File Risk Analysis", "Complete Scan History Log"].map((fitur, i) => (
                <div key={i} className="flex items-center gap-3 text-slate-200 text-sm font-medium">
                  <CheckCircle2 className="w-5 h-5 text-blue-500 shrink-0" /> {fitur}
                </div>
              ))}
            </div>
          </div>

          <div className="flex-1 w-full max-w-sm mx-auto relative group">
            <div className="absolute inset-0 bg-blue-500/20 blur-[70px] rounded-full group-hover:bg-blue-500/30 transition-all duration-700"></div>
            
            <div className="relative bg-[#0b1320] border-8 border-slate-800 rounded-[2.5rem] h-[580px] overflow-hidden shadow-2xl flex flex-col ring-1 ring-white/10 animate-[pulse_4s_ease-in-out_infinite] hover:animate-none transition-all">
              <div className="absolute top-0 w-full h-7 flex justify-center z-20">
                <div className="w-1/3 h-5 bg-slate-800 rounded-b-xl"></div>
              </div>

              <div className="p-6 pt-12 flex flex-col h-full bg-gradient-to-b from-[#111c30] to-[#0b1320] relative">
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-blue-500/10 rounded-full blur-2xl"></div>

                <div className="flex justify-between items-center mb-6">
                  <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center border border-slate-700">
                    <Fingerprint className="w-5 h-5 text-blue-400 animate-pulse" />
                  </div>
                  <span className="text-xs font-mono font-bold text-blue-400 border border-blue-900 bg-blue-950/60 px-3 py-1 rounded-full flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-blue-400 animate-ping"></span> Level 3
                  </span>
                </div>

                <div className="text-center mb-6">
                  <h4 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500 tracking-widest font-mono drop-shadow-md">OXXA</h4>
                  <span className="text-[10px] font-mono text-slate-400 tracking-widest font-bold">SYSTEM ACTIVE</span>
                </div>
                
                <div className="space-y-3">
                  <div className="p-3.5 bg-slate-900/90 rounded-2xl border border-slate-700/60 flex justify-between items-center backdrop-blur-sm">
                    <div>
                      <p className="text-[10px] font-mono text-slate-400 uppercase tracking-wider">Device Status</p>
                      <p className="text-sm font-bold text-white">Samsung SM-F95</p>
                    </div>
                    <Smartphone className="text-blue-500 w-5 h-5" />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3.5 bg-slate-900/90 rounded-2xl border border-slate-700/60 text-center">
                      <p className="text-[10px] font-mono text-slate-400 uppercase tracking-wider mb-1">Jaringan</p>
                      <p className="text-sm font-bold text-emerald-400 flex justify-center items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span> Terhubung</p>
                    </div>
                    <div className="p-3.5 bg-slate-900/90 rounded-2xl border border-slate-700/60 text-center">
                      <p className="text-[10px] font-mono text-slate-400 uppercase tracking-wider mb-1">Temperatur</p>
                      <p className="text-sm font-bold text-blue-400">Cool</p>
                    </div>
                  </div>

                  <div className="p-3.5 bg-slate-900/90 rounded-2xl border border-slate-700/60">
                     <p className="text-[10px] font-mono text-slate-400 uppercase tracking-wider mb-1">Informasi Pengguna</p>
                     <div className="flex justify-between items-center">
                       <span className="text-sm font-bold text-white">user8</span>
                       <span className="text-xs text-blue-400 font-mono">628123456XXX</span>
                     </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Enterprise Solutions */}
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-xs font-mono font-black text-blue-500 tracking-[0.2em] mb-4">// ENTERPRISE SOLUTIONS</h2>
            <h3 className="text-3xl md:text-4xl font-bold text-white">INTELLIGENCE. HUMAN ACCOUNTABILITY.</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="group rounded-3xl bg-slate-900/60 backdrop-blur-md border border-slate-800 p-8 hover:bg-slate-900 hover:border-purple-500/50 transition-colors">
              <div className="w-14 h-14 rounded-2xl bg-purple-900/30 flex items-center justify-center border border-purple-500/30 mb-6">
                <Cpu className="text-purple-400 w-7 h-7" />
              </div>
              <h4 className="text-2xl font-bold text-white mb-2">AI Customer Service</h4>
              <p className="text-purple-400 text-xs font-mono font-bold tracking-widest mb-4">INTERACTIVE DIGITAL HUMAN</p>
              <p className="text-slate-400 mb-6 text-sm leading-relaxed">
                <strong className="text-white">Conversational AI:</strong> Komunikasi dua arah melalui manusia digital yang responsif dan cerdas, mewakili identitas layanan Anda.
              </p>
              <div className="w-full h-40 bg-[#0a101d] rounded-xl border border-slate-800 overflow-hidden relative flex items-center justify-center">
                 <div className="absolute inset-0 bg-purple-500/10"></div>
                 <MonitorPlay className="w-12 h-12 text-purple-900 animate-pulse" />
              </div>
            </div>

            <div className="group rounded-3xl bg-slate-900/60 backdrop-blur-md border border-slate-800 p-8 hover:bg-slate-900 hover:border-blue-500/50 transition-colors">
              <div className="w-14 h-14 rounded-2xl bg-blue-900/30 flex items-center justify-center border border-blue-500/30 mb-6">
                <Network className="text-blue-400 w-7 h-7" />
              </div>
              <h4 className="text-2xl font-bold text-white mb-2">Command Centre</h4>
              <p className="text-blue-400 text-xs font-mono font-bold tracking-widest mb-4">INTEGRATED OPERATIONS</p>
              <p className="text-slate-400 mb-6 text-sm leading-relaxed">
                <strong className="text-white">Security Intelligence:</strong> Analisis pola, anomali, dan prioritas risiko dalam satu dasbor komprehensif. Keputusan penting tetap dalam kendali manusia.
              </p>
              <div className="w-full h-40 bg-[#0a101d] rounded-xl border border-slate-800 overflow-hidden relative flex items-center justify-center">
                 <div className="absolute inset-0 bg-blue-500/10 bg-[linear-gradient(to_right,#1e3a8a33_1px,transparent_1px),linear-gradient(to_bottom,#1e3a8a33_1px,transparent_1px)] bg-[size:10px_10px]"></div>
                 <Activity className="w-12 h-12 text-blue-500 animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Core Capabilities */}
      <section id="capabilities" className="py-28 px-6 max-w-7xl mx-auto border-t border-slate-800">
        <div className="text-center mb-16">
          <h2 className="text-xs font-mono font-black text-blue-500 tracking-[0.2em] mb-4">// CORE CAPABILITIES</h2>
          <p className="text-3xl md:text-5xl font-bold text-white">FIVE DISCIPLINES. ONE ACCOUNTABLE PARTNER.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-8">
          <div className="p-8 rounded-3xl bg-slate-900/70 backdrop-blur-md border border-slate-800 hover:border-blue-500/60 transition-all duration-300 flex flex-col justify-between group">
            <div>
              <div className="w-14 h-14 rounded-2xl bg-blue-950 flex items-center justify-center border border-blue-800/50 mb-6 group-hover:scale-110 transition-transform">
                <ShieldAlert className="w-7 h-7 text-blue-400" />
              </div>
              <span className="text-[10px] font-mono font-black tracking-widest text-blue-500 uppercase">Discipline 01</span>
              <h3 className="text-2xl font-bold text-white mt-1 mb-4">Cyber Security</h3>
              <p className="text-slate-400 text-sm leading-relaxed mb-6">
                Membantu organisasi mengidentifikasi eksposur, memperkuat kontrol, mengintegrasikan teknologi keamanan, serta menyiapkan respons cepat saat insiden terjadi.
              </p>
            </div>
            <ul className="space-y-2 border-t border-slate-800/80 pt-4 text-xs font-mono text-slate-300">
              <li className="flex items-center gap-2">🔹 Vulnerability Assessment</li>
              <li className="flex items-center gap-2">🔹 Incident Response Strategy</li>
              <li className="flex items-center gap-2">🔹 Secure Development Control</li>
            </ul>
          </div>

          <div className="p-8 rounded-3xl bg-slate-900/70 backdrop-blur-md border border-slate-800 hover:border-blue-500/60 transition-all duration-300 flex flex-col justify-between group">
            <div>
              <div className="w-14 h-14 rounded-2xl bg-indigo-950 flex items-center justify-center border border-indigo-800/50 mb-6 group-hover:scale-110 transition-transform">
                <Search className="w-7 h-7 text-indigo-400" />
              </div>
              <span className="text-[10px] font-mono font-black tracking-widest text-indigo-500 uppercase">Discipline 02</span>
              <h3 className="text-2xl font-bold text-white mt-1 mb-4">Digital Forensic</h3>
              <p className="text-slate-400 text-sm leading-relaxed mb-6">
                Investigasi mendalam terhadap artefak digital, analisis bukti elektronik, dan penelusuran jejak pelanggaran keamanan untuk kebutuhan mitigasi internal.
              </p>
            </div>
            <ul className="space-y-2 border-t border-slate-800/80 pt-4 text-xs font-mono text-slate-300">
              <li className="flex items-center gap-2">🔹 Electronic Evidence Recovery</li>
              <li className="flex items-center gap-2">🔹 Malware & Threat Analysis</li>
              <li className="flex items-center gap-2">🔹 Chain of Custody Report</li>
            </ul>
          </div>

          <div className="p-8 rounded-3xl bg-slate-900/70 backdrop-blur-md border border-slate-800 hover:border-purple-500/60 transition-all duration-300 flex flex-col justify-between group">
            <div>
              <div className="w-14 h-14 rounded-2xl bg-purple-950 flex items-center justify-center border border-purple-800/50 mb-6 group-hover:scale-110 transition-transform">
                <Cpu className="w-7 h-7 text-purple-400" />
              </div>
              <span className="text-[10px] font-mono font-black tracking-widest text-purple-500 uppercase">Discipline 03</span>
              <h3 className="text-2xl font-bold text-white mt-1 mb-4">Artificial Intelligence</h3>
              <p className="text-slate-400 text-sm leading-relaxed mb-6">
                Pemanfaatan machine learning dan otomasi cerdas untuk mempercepat pemahaman konteks, mendeteksi anomali, serta menghadirkan interaksi digital human.
              </p>
            </div>
            <ul className="space-y-2 border-t border-slate-800/80 pt-4 text-xs font-mono text-slate-300">
              <li className="flex items-center gap-2">🔹 Conversational AI & Digital Human</li>
              <li className="flex items-center gap-2">🔹 Security Pattern Intelligence</li>
              <li className="flex items-center gap-2">🔹 Responsible AI Governance</li>
            </ul>
          </div>

          <div className="p-8 rounded-3xl bg-slate-900/70 backdrop-blur-md border border-slate-800 hover:border-blue-500/60 transition-all duration-300 flex flex-col justify-between group">
            <div>
              <div className="w-14 h-14 rounded-2xl bg-blue-950 flex items-center justify-center border border-blue-800/50 mb-6 group-hover:scale-110 transition-transform">
                <Code className="w-7 h-7 text-blue-400" />
              </div>
              <span className="text-[10px] font-mono font-black tracking-widest text-blue-500 uppercase">Discipline 04</span>
              <h3 className="text-2xl font-bold text-white mt-1 mb-4">Software Development</h3>
              <p className="text-slate-400 text-sm leading-relaxed mb-6">
                Pengembangan perangkat lunak kustom yang dibangun dengan prinsip *Security by Design*, memastikan ketahanan aplikasi dari tahap penulisan kode awal.
              </p>
            </div>
            <ul className="space-y-2 border-t border-slate-800/80 pt-4 text-xs font-mono text-slate-300">
              <li className="flex items-center gap-2">🔹 Secure Coding Standards</li>
              <li className="flex items-center gap-2">🔹 Scalable Web & Mobile Apps</li>
              <li className="flex items-center gap-2">🔹 Enterprise Architecture</li>
            </ul>
          </div>

          <div className="p-8 rounded-3xl bg-slate-900/70 backdrop-blur-md border border-slate-800 hover:border-indigo-500/60 transition-all duration-300 flex flex-col justify-between group">
            <div>
              <div className="w-14 h-14 rounded-2xl bg-indigo-950 flex items-center justify-center border border-indigo-800/50 mb-6 group-hover:scale-110 transition-transform">
                <Network className="w-7 h-7 text-indigo-400" />
              </div>
              <span className="text-[10px] font-mono font-black tracking-widest text-indigo-500 uppercase">Discipline 05</span>
              <h3 className="text-2xl font-bold text-white mt-1 mb-4">System Integrator</h3>
              <p className="text-slate-400 text-sm leading-relaxed mb-6">
                Menyatukan perangkat, jaringan, infrastruktur, dan ekosistem perangkat lunak yang terfragmentasi menjadi satu kesatuan operasional yang solid.
              </p>
            </div>
            <ul className="space-y-2 border-t border-slate-800/80 pt-4 text-xs font-mono text-slate-300">
              <li className="flex items-center gap-2">🔹 Ecosystem Harmonization</li>
              <li className="flex items-center gap-2">🔹 Command Centre Integration</li>
              <li className="flex items-center gap-2">🔹 Infrastructure Resilience</li>
            </ul>
          </div>

          <div className="p-8 rounded-3xl bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 flex flex-col justify-center items-center text-center shadow-xl shadow-blue-900/20 relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_var(--tw-gradient-stops))] from-white/10 to-transparent"></div>
            <div className="relative z-10">
              <Lock className="w-12 h-12 text-white/90 mx-auto mb-4" />
              <h3 className="text-2xl font-black text-white mb-3 leading-tight font-mono">SECURITY FIRST.</h3>
              <p className="text-blue-100 text-sm font-medium leading-relaxed">
                Every capability is designed around security, accountability, and operational relevance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Footer & Contact */}
      <footer id="contact" className="py-24 px-6 bg-[#03070f] border-t border-slate-800 relative overflow-hidden">
        <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-blue-900/10 to-transparent pointer-events-none"></div>
        
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between gap-16 relative z-10">
          <div className="flex-1">
            <h2 className="text-4xl md:text-7xl font-black text-white leading-[1.1] mb-6">
              LET&apos;S SECURE <br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-500">WHAT MATTERS.</span>
            </h2>
            <p className="text-slate-400 max-w-md text-lg leading-relaxed">
              Ceritakan sistem, risiko, atau tantangan operasional yang sedang Anda hadapi. Tim JuraganIT akan membantu memetakan langkah yang relevan.
            </p>
          </div>

          <div className="flex-1 space-y-6 md:pl-16">
            <a href="https://wa.me/6282312795520" className="group flex items-center gap-6 p-4 rounded-2xl hover:bg-slate-900/80 transition-colors border border-transparent hover:border-slate-800">
              <div className="w-14 h-14 bg-slate-900 border border-slate-800 rounded-xl flex items-center justify-center group-hover:border-blue-500 group-hover:bg-blue-950/50 transition-colors">
                <Phone className="text-blue-500 w-6 h-6" />
              </div>
              <div>
                <p className="text-[10px] font-mono text-slate-500 font-bold tracking-widest mb-1">WHATSAPP / PHONE</p>
                <p className="text-xl font-bold text-white group-hover:text-blue-400 transition-colors font-mono">0823 1279 5520</p>
              </div>
            </a>

            <a href="mailto:juraganlangitteknologi@gmail.com" className="group flex items-center gap-6 p-4 rounded-2xl hover:bg-slate-900/80 transition-colors border border-transparent hover:border-slate-800">
              <div className="w-14 h-14 bg-slate-900 border border-slate-800 rounded-xl flex items-center justify-center group-hover:border-blue-500 group-hover:bg-blue-950/50 transition-colors">
                <Mail className="text-blue-500 w-6 h-6" />
              </div>
              <div>
                <p className="text-[10px] font-mono text-slate-500 font-bold tracking-widest mb-1">EMAIL</p>
                <p className="text-lg md:text-xl font-bold text-white group-hover:text-blue-400 transition-colors break-all font-mono">juraganlangitteknologi@gmail.com</p>
              </div>
            </a>

            <div className="flex items-start gap-6 p-4">
              <div className="w-14 h-14 bg-slate-900 border border-slate-800 rounded-xl flex items-center justify-center shrink-0">
                <MapPin className="text-blue-500 w-6 h-6" />
              </div>
              <div>
                <p className="text-[10px] font-mono text-slate-500 font-bold tracking-widest mb-2">OFFICE</p>
                <p className="text-sm font-medium text-slate-400 leading-relaxed">
                  Jl. Palem Raya, Ruko Barcelona No. 80, <br/> Cibodas, Kota Tangerang, Banten 15138
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto mt-24 pt-8 border-t border-slate-800 flex justify-between items-center text-[10px] font-mono text-slate-600 font-bold tracking-widest relative z-10">
           <p>© 2026 PT JURAGAN LANGIT TEKNOLOGI.</p>
           <p>BUILT TO DEFEND.</p>
        </div>
      </footer>

    </div>
  );
}
